import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ref, get, update, push, remove, database } from '../firebase';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiThumbsUp, FiShare2, FiSend, FiClock } from 'react-icons/fi';

const Photoshow = () => {
  const { photoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const commentInputRef = useRef(null);

  // State
  const [photo, setPhoto] = useState(location.state?.photo || null);
  const [commissionConfig, setCommissionConfig] = useState(null);
  const [watchTime, setWatchTime] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [showToast, setShowToast] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const [photoLikes, setPhotoLikes] = useState({});
  const [photoLikeCount, setPhotoLikeCount] = useState(0);
  const [hasLikedPhoto, setHasLikedPhoto] = useState(false);
  const [photoComments, setPhotoComments] = useState([]);
  const [newPhotoComment, setNewPhotoComment] = useState('');
  const [photoCommentCount, setPhotoCommentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [giftsConfig, setGiftsConfig] = useState(null);
  const [currentCommentIndex, setCurrentCommentIndex] = useState(0);
  const [shareCount, setShareCount] = useState(0);

  // Get current user
  const getCurrentUser = () => {
    const userData = JSON.parse(localStorage.getItem('currentUser'));
    return {
      mobile: userData?.mobile || '',
      username: userData?.username || userData?.mobile || 'Anonymous',
      name: userData?.name || userData?.fullName || '',
      surname: userData?.surname || '',
    };
  };

  // Check login
  useEffect(() => {
    const checkLogin = () => {
      const userData = localStorage.getItem('currentUser');
      setIsLoggedIn(!!userData);
    };
    checkLogin();
    window.addEventListener('storage', checkLogin);
    return () => window.removeEventListener('storage', checkLogin);
  }, []);

  // Fetch commission config and gifts config
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        // Commission config
        const commissionRef = ref(database, 'videoplayer/Commissions/1');
        const commissionSnap = await get(commissionRef);
        if (commissionSnap.exists()) {
          setCommissionConfig(commissionSnap.val());
        } else {
          setCommissionConfig({ photoTime: 0, photoPoints: 0 });
        }
        // Gifts config
        const giftsRef = ref(database, 'videoplayer/Gifts/1');
        const giftsSnap = await get(giftsRef);
        if (giftsSnap.exists()) {
          setGiftsConfig(giftsSnap.val());
        } else {
          setGiftsConfig({ likePoints: 0, sharePoints: 0 });
        }
      } catch (err) {
        toast.error('Failed to load reward configuration.');
      }
    };
    fetchConfigs();
  }, []);

  // Fetch photo data if not in state
  useEffect(() => {
    if (photo) {
      setLoading(false);
      return;
    }
    const fetchPhoto = async () => {
      setLoading(true);
      try {
        const photosRef = ref(database, 'videoplayer/photoUpload');
        const snapshot = await get(photosRef);
        let foundPhoto = null;
        let userMobile = '';
        if (snapshot.exists()) {
          Object.entries(snapshot.val()).some(([mobile, userPhotos]) => {
            return Object.entries(userPhotos).some(([photoKey, p]) => {
              if (p.id === photoId || photoKey === photoId || (p.photoURL && p.photoURL.includes(photoId))) {
                foundPhoto = { ...p, dbKey: photoKey, channelMobile: mobile.replace(/[_]/g, '') };
                userMobile = mobile;
                return true;
              }
              return false;
            });
          });
        }
        if (foundPhoto) {
          setPhoto(foundPhoto);
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toast.error('Failed to load photo.');
      }
    };
    fetchPhoto();
  }, [photo, photoId]);

  // Fetch uploader profile, likes, comments
  useEffect(() => {
    if (!photo) return;
    const fetchDetails = async () => {
      const sanitizedMobile = photo.channelMobile?.replace(/[_]/g, '_');
      const profileRef = ref(database, `videoplayer/UserProfile/${sanitizedMobile}`);
      const userRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
      const [profileSnapshot, userSnapshot] = await Promise.all([
        get(profileRef),
        get(userRef),
      ]);
      setUserProfile({
        profilePic: profileSnapshot.exists() ? profileSnapshot.val().profilePic : null,
        name: userSnapshot.exists() ? userSnapshot.val().name || userSnapshot.val().fullName || photo.channelMobile : photo.channelMobile,
      });

      // Likes
      const likesRef = ref(database, `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/likes`);
      const likesSnapshot = await get(likesRef);
      if (likesSnapshot.exists()) {
        const likesData = likesSnapshot.val();
        setPhotoLikes(likesData);
        setPhotoLikeCount(Object.keys(likesData).length);
        if (isLoggedIn) {
          const currentUser = getCurrentUser();
          const sanitizedCurrentUserMobile = currentUser.mobile.replace(/[^0-9]/g, '_');
          setHasLikedPhoto(!!likesData[sanitizedCurrentUserMobile]);
        }
      } else {
        setPhotoLikes({});
        setPhotoLikeCount(0);
        setHasLikedPhoto(false);
      }

      // Comments
      const commentsRef = ref(database, `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/comments`);
      const commentsSnapshot = await get(commentsRef);
      if (commentsSnapshot.exists()) {
        const commentsData = commentsSnapshot.val();
        const commentsArray = Object.entries(commentsData).map(([key, comment]) => ({
          id: key,
          ...comment,
        }));
        setPhotoComments(commentsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
        setPhotoCommentCount(commentsArray.length);
      } else {
        setPhotoComments([]);
        setPhotoCommentCount(0);
      }

      // Fetch share count
      const sharesRef = ref(database, `videoplayer/photoUpload/${sanitizedMobile}/${photo.dbKey}/shares`);
      const sharesSnapshot = await get(sharesRef);
      if (sharesSnapshot.exists()) {
        setShareCount(Object.keys(sharesSnapshot.val()).length);
      } else {
        setShareCount(0);
      }
    };
    fetchDetails();
    // eslint-disable-next-line
  }, [photo, isLoggedIn]);

  // Watch time tracking and points awarding
  useEffect(() => {
    if (!isLoggedIn || !photo || !commissionConfig) return;
    const requiredTimeMinutes = commissionConfig.photoTime || 0;
    const requiredTimeSeconds = requiredTimeMinutes * 60;
    const pointsToAdd = commissionConfig.photoPoints || 0;
    let awarded = false;
    let intervalId = setInterval(() => {
      setWatchTime(prev => {
        const newTime = prev + 1;
        if (
          !awarded &&
          requiredTimeSeconds > 0 &&
          pointsToAdd > 0 &&
          newTime % requiredTimeSeconds === 0
        ) {
          awardPoints(pointsToAdd, 'photo', 'Photo Watch Reward');
          awarded = true;
        }
        return newTime;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isLoggedIn, photo, commissionConfig]);

  // Award points
  const awardPoints = async (points, type, giftName) => {
    if (!points || points <= 0) return;
    const { mobile } = getCurrentUser();
    const sanitizedMobile = mobile.replace(/[^0-9]/g, '_');
    const userPointsRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
    try {
      const pointsSnapshot = await get(userPointsRef);
      const currentPoints = pointsSnapshot.exists() ? pointsSnapshot.val().points || 0 : 0;
      const newPoints = currentPoints + points;
      await update(userPointsRef, { points: newPoints });
      setPointsEarned(newPoints);
      setShowToast({ points, type });
      setTimeout(() => setShowToast(null), 3000);
      toast.success(`+${points} points for ${type}!`);
    } catch (err) {
      toast.error(`Failed to award points for ${type}.`);
    }
  };

  // Like handler
  const handlePhotoLike = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: `/photo/${photoId}` } });
      return;
    }
    if (!photo) return;
    try {
      const currentUser = getCurrentUser();
      const sanitizedCurrentUserMobile = currentUser.mobile.replace(/[^0-9]/g, '_');
      const sanitizedPhotoMobile = photo.channelMobile.replace(/[^0-9]/g, '_');
      const likeRef = ref(database, `videoplayer/photoUpload/${sanitizedPhotoMobile}/${photo.dbKey}/likes/${sanitizedCurrentUserMobile}`);
      if (hasLikedPhoto) {
        await remove(likeRef);
        setHasLikedPhoto(false);
        setPhotoLikeCount(prev => prev - 1);
        setPhotoLikes(prev => {
          const newLikes = { ...prev };
          delete newLikes[sanitizedCurrentUserMobile];
          return newLikes;
        });
        toast.success('Like removed!');
      } else {
        await update(likeRef, { timestamp: new Date().toISOString() });
        setHasLikedPhoto(true);
        setPhotoLikeCount(prev => prev + 1);
        setPhotoLikes(prev => ({ ...prev, [sanitizedCurrentUserMobile]: { timestamp: new Date().toISOString() } }));
        toast.success('Photo liked!');
        // Removed awarding points for liking
        // if (giftsConfig?.likePoints > 0) {
        //   await awardPoints(giftsConfig.likePoints, 'like', 'Photo Like Reward');
        // }
      }
    } catch (err) {
      toast.error('Failed to update like status.');
    }
  };

  // Comment handler
  const handlePhotoComment = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/login', { state: { from: `/photo/${photoId}` } });
      return;
    }
    if (!newPhotoComment.trim() || !photo) return;
    try {
      const currentUser = getCurrentUser();
      const sanitizedPhotoMobile = photo.channelMobile.replace(/[^0-9]/g, '_');
      const commentsRef = ref(database, `videoplayer/photoUpload/${sanitizedPhotoMobile}/${photo.dbKey}/comments`);
      const newCommentRef = push(commentsRef);
      
      // Get user's full name from their profile
      const userProfileRef = ref(database, `videoplayer/UserProfile/${currentUser.mobile.replace(/[^0-9]/g, '_')}`);
      const userProfileSnap = await get(userProfileRef);
      const userData = userProfileSnap.exists() ? userProfileSnap.val() : {};
      
      const commentData = {
        text: newPhotoComment.trim().replace(/[<>&]/g, ''),
        username: userData.name || currentUser.username || currentUser.mobile,
        mobile: currentUser.mobile,
        timestamp: new Date().toISOString(),
      };
      
      await update(newCommentRef, commentData);
      setPhotoComments(prev => [{ id: newCommentRef.key, ...commentData }, ...prev]);
      setPhotoCommentCount(prev => prev + 1);
      setNewPhotoComment('');
      toast.success('Comment posted successfully!');
    } catch (err) {
      console.error('Error posting comment:', err);
      toast.error('Failed to post comment.');
    }
  };
  
  // Fetch commenter's name
  const fetchCommenterName = async (mobile) => {
    try {
      const sanitizedMobile = mobile.replace(/[^0-9]/g, '_');
      const userProfileRef = ref(database, `videoplayer/UserProfile/${sanitizedMobile}`);
      const userRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
      
      const [profileSnap, userSnap] = await Promise.all([
        get(userProfileRef),
        get(userRef)
      ]);
      
      if (userSnap.exists()) {
        const userData = userSnap.val();
        return userData.name || userData.fullName || userData.username || mobile;
      }
      return mobile; // Return mobile as fallback
    } catch (error) {
      console.error('Error fetching commenter name:', error);
      return mobile; // Return mobile as fallback
    }
  };
  
  // Update comments with names when loaded
  useEffect(() => {
    if (photoComments.length > 0) {
      const updateCommentNames = async () => {
        const updatedComments = await Promise.all(photoComments.map(async (comment) => {
          if (comment.mobile && (!comment.username || comment.username === comment.mobile)) {
            const name = await fetchCommenterName(comment.mobile);
            return { ...comment, username: name };
          }
          return comment;
        }));
        
        // Only update if there are changes to prevent infinite loop
        if (JSON.stringify(updatedComments) !== JSON.stringify(photoComments)) {
          setPhotoComments(updatedComments);
        }
      };
      
      updateCommentNames();
    }
  }, [photoComments.length]);

  // Share handler
  // const handlePhotoShare = async () => {
  //   if (!isLoggedIn) {
  //     navigate('/login', { state: { from: `/photo/${photoId}` } });
  //     return;
  //   }
  //   if (!photo) return;
  //   try {
  //     const currentUser = getCurrentUser();
  //     const sanitizedPhotoMobile = photo.channelMobile.replace(/[^0-9]/g, '_');
  //     const sanitizedCurrentUserMobile = currentUser.mobile.replace(/[^0-9]/g, '_');
      
  //     // Store share data in Firebase
  //     const shareRef = ref(database, `videoplayer/photoUpload/${sanitizedPhotoMobile}/${photo.dbKey}/shares/${sanitizedCurrentUserMobile}`);
  //     const shareData = {
  //       mobile: currentUser.mobile,
  //       timestamp: new Date().toISOString(),
  //     };
  //     await update(shareRef, shareData);
  //     setShareCount((prev) => prev + 1);
      
  //     const shareUrl = `${window.location.origin}/photo/${photoId}`;
  //     if (navigator.share) {
  //       await navigator.share({
  //         title: photo.title || 'Check out this photo',
  //         url: shareUrl,
  //       });
  //     } else {
  //       await navigator.clipboard.writeText(shareUrl);
  //       toast.success('Link copied to clipboard!');
  //     }
  //     // Removed awarding points for sharing
  //     // if (giftsConfig?.sharePoints > 0) {
  //     //   await awardPoints(giftsConfig.sharePoints, 'share', 'Photo Share Reward');
  //     // }
  //   } catch (err) {
  //     toast.error('Failed to share photo.');
  //   }
  // };


// Share handler
const handlePhotoShare = async () => {
  if (!isLoggedIn) {
    navigate('/login', { state: { from: `/photo/${photoId}` } });
    return;
  }
  if (!photo) return;
  try {
    const currentUser = getCurrentUser();
    const sanitizedPhotoMobile = photo.channelMobile.replace(/[^0-9]/g, '_');
    const sanitizedCurrentUserMobile = currentUser.mobile.replace(/[^0-9]/g, '_');

    // Store share data in Firebase
    const shareRef = ref(database, `videoplayer/photoUpload/${sanitizedPhotoMobile}/${photo.dbKey}/shares/${sanitizedCurrentUserMobile}`);
    const shareData = {
      mobile: currentUser.mobile,
      timestamp: new Date().toISOString(),
    };
    await update(shareRef, shareData);
    setShareCount((prev) => prev + 1);

    // Fetch the uploader's username from /videoplayer/Users
    const userRef = ref(database, `videoplayer/Users/${sanitizedPhotoMobile}`);
    const userSnapshot = await get(userRef);
    let username = 'Anonymous'; // Fallback username
    if (userSnapshot.exists()) {
      const userData = userSnapshot.val();
      username = userData.username || userData.name || userData.fullName || userData.mobile || 'Anonymous';
    }

    // Create a clean username for the URL (replace spaces and special characters)
    const cleanUsername = username.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();

    // Construct the share URL with photoId and username
    // const shareUrl = `${window.location.origin}/photo/${cleanUsername}`;
    const shareUrl = `${window.location.origin}`;

    if (navigator.share) {
      await navigator.share({
        title: photo.title || 'Check out this photo',
        url: shareUrl,
      });
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  } catch (err) {
    console.error('Error sharing photo:', err);
    toast.error('Failed to share photo.');
  }
};

  // Avatar generator
  const generateAvatar = (name, size = 40) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD',
      '#D4A5A5', '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71',
    ];
    const char = name?.charAt(0)?.toUpperCase() || 'A';
    const color = colors[char.charCodeAt(0) % colors.length];
    return (
      <div
        className="flex items-center justify-center rounded-full text-white font-semibold"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: color,
          fontSize: `${size * 0.5}px`,
        }}
      >
        {char}
      </div>
    );
  };

  // Relative time
  const getRelativeTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
    if (diffInSeconds < 60) return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  };

  // Format watch time
  const formatWatchTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} min`;
  };

  // On close, clear and go back
  const handleClose = () => {
    navigate(-1);
  };

  // Auto-slide comments every 2 seconds
  useEffect(() => {
    if (photoComments.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentCommentIndex((prev) => (prev + 1) % photoComments.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [photoComments]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-300 text-xl font-medium">Loading photo...</div>
      </div>
    );
  }

  if (!photo || !photo.photoURL) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-red-500 dark:text-red-400 text-xl font-medium">No photo data found.</div>
        <button onClick={handleClose} className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md">Go Back</button>
      </div>
    );
  }

  const requiredTimeSeconds = (commissionConfig?.photoTime || 0) * 60;
  const progressPercent = requiredTimeSeconds ? Math.min(100, (watchTime / requiredTimeSeconds) * 100) : 0;
  const hasProfilePic = userProfile.profilePic && !userProfile.profilePic.includes('placeholder.com');

  return (
    <main className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        {/* Removed Back button */}
        {/* Full-width white background for image and info */}
        <div className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-8 overflow-x-auto">
          <div className="flex flex-col items-center w-full">
            <img
              src={photo.photoURL}
              alt={photo.title}
              className="mb-4 rounded-lg border-2 border-blue-200 shadow-lg max-w-3xl w-auto max-w-full object-contain"
              style={{ maxWidth: '100%', maxHeight: '60vh' }}
            />
            {/* Info below image, left-aligned */}
            <div className="w-full max-w-2xl flex flex-col items-start">
              <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-left">{photo.title}</h1>
              <div className="flex items-center mb-2">
                <div className="relative">
                  {hasProfilePic ? (
                    <img
                      src={userProfile.profilePic}
                      alt={userProfile.username}
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    generateAvatar(userProfile.username, 40)
                  )}
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {userProfile.name}
                  </p>
                </div>
              </div>
              {photo.description && (
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl shadow w-full">
                  <p className="text-gray-700 dark:text-gray-200 text-base sm:text-lg text-left">{photo.description}</p>
                </div>
              )}
            </div>
          </div>
          {/* Timeline Points */}
          {isLoggedIn && (
            <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-900 rounded-lg shadow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <FiClock className="text-blue-500" size={18} />
                <span>
                  Watch Time: <span className="font-medium">{formatWatchTime(watchTime)}</span>
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                <span>Points: <span className="font-bold">{pointsEarned}</span></span>
                {showToast && (
                  <div className="relative">
                    <div className="absolute -top-6 bg-blue-500 text-white text-xs rounded-full px-2 py-1 animate-bounce">
                      +{showToast.points} ({showToast.type})
                    </div>
                  </div>
                )}
              </div>
              <div className="w-full sm:w-1/3">
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-1000 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>{formatWatchTime(watchTime)}</span>
                  <span>{formatWatchTime(requiredTimeSeconds)}</span>
                </div>
              </div>
            </div>
          )}
          {/* Like, Share */}
          <div className="flex items-center gap-3 mt-6 flex-wrap justify-center">
            <button
              onClick={handlePhotoLike}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                hasLikedPhoto
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <FiThumbsUp className="mr-2" size={16} />
              {photoLikeCount}
            </button>
            <button
              onClick={handlePhotoShare}
              className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md font-medium transition-all duration-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              <FiShare2 className="mr-2" size={16} />
              Share
              <span className="ml-2 text-xs text-gray-500">{shareCount}</span>
            </button>
          </div>
          {/* Comments Section */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Comments ({photoCommentCount})
            </h3>
            {isLoggedIn ? (
              <form onSubmit={handlePhotoComment} className="flex items-start gap-3 mb-6">
                <div className="flex-1">
                  <textarea
                    ref={commentInputRef}
                    value={newPhotoComment}
                    onChange={(e) => setNewPhotoComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium transition-all duration-300 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!newPhotoComment.trim()}
                >
                  <FiSend size={16} />
                </button>
              </form>
            ) : (
              <div className="mb-6 text-gray-600 dark:text-gray-300">
                Please <a href="/login" className="text-blue-600 dark:text-blue-400 hover:underline">sign in</a> to post a comment.
              </div>
            )}
            <div className="space-y-4">
              {photoComments.length === 0 && (
                <div className="text-gray-500 dark:text-gray-400 text-center">No comments yet.</div>
              )}
              {photoComments.length > 0 && (
                <div key={photoComments[currentCommentIndex]?.id} className="flex gap-3 transition-all duration-500">
                  <div className="flex-shrink-0">
                    {generateAvatar(photoComments[currentCommentIndex]?.username, 36)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">{photoComments[currentCommentIndex]?.username}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{getRelativeTimeAgo(photoComments[currentCommentIndex]?.timestamp)}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{photoComments[currentCommentIndex]?.text}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Photoshow;
